import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { useState, useEffect, useCallback } from 'react';

import If from 'global/ErrorHandlers/If';
import Image from 'global/Image';
import useRegistrationStatus from 'features/stream/containers/RegistrationStatus/store';
import { checkIsWaitingAccount } from 'features/shop-revamp/utils';
import { selectors as profileSelectors } from 'lib/entities/profile';
import { selectors, thunks } from 'lib/entities/shop';

import OfferingSubscription from '../Modal/OfferingSubscription';
import {
  HeaderText,
  DescriptionText,
  ButtonNewText,
  ButtonContinueText,
} from '../constants';

import {
  PaywallWrapper,
  TextWrapper,
  Header,
  Description,
  ButtonWrapper,
  ButtonNew,
  ButtonContinue,
} from '../styled';
import useStorePaywall from '../store';
import PaywallAnalytics from '../analytics';

const LockedIcon = '/icon/paywall/locked.svg';

interface IPaywallProps {
  source: string;
  height?: string;
  className?: string;
  isRenewal: boolean;
  isWidget?: boolean;
  symbol?: string;
}

function Paywall({
  source,
  height,
  className,
  isRenewal,
  isWidget = false,
  symbol = '',
}: IPaywallProps) {
  const dispatch = useDispatch();
  const router = useRouter();
  const { data, getRegistrationStatus } = useRegistrationStatus();
  const { page, company, widget } = useStorePaywall();
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  const {
    products: { list },
    cart: { isLoading },
  } = useSelector(selectors);
  const {
    data: { ispro: isPro },
  } = useSelector(profileSelectors);

  useEffect(() => {
    dispatch(thunks.fetchProducts());
    getRegistrationStatus();
  }, []);

  const getLatestSubscription = useCallback(() => {
    const { data: pageData } = page || {};
    const { data: companyData } = company || {};
    const { data: widgetData } = widget || {};
    const lastSubscriptionData =
      pageData?.last_subscription ||
      companyData?.last_subscription ||
      widgetData?.last_subscription;

    const selectedList = list?.find(
      (item) => Number(item.id) === lastSubscriptionData?.id,
    );

    dispatch(thunks.continueSubscribe({ selected: selectedList }));
  }, [list]);

  const analyticsPayload = {
    oldStatus: Boolean(isPro),
    source,
    companyID: symbol,
  };

  const handleSetModal = (visible: boolean): void => {
    setModalVisible(visible);
  };

  const handleOpenAccess = (): void => {
    handleSetModal(true);

    PaywallAnalytics.onClickOpenAccess(analyticsPayload);
  };

  const handleExtendAccess = (): void => {
    PaywallAnalytics.onClickExtendSubscribe(analyticsPayload);

    if (checkIsWaitingAccount(data?.status)) {
      router.push('/shop');
      return;
    }

    getLatestSubscription();
  };

  return (
    <>
      <PaywallWrapper
        className={className}
        style={{ height: height || 'auto' }}
      >
        <Image src={LockedIcon} alt="Paywall" />

        <TextWrapper isWidget={isWidget}>
          <Header>{HeaderText}</Header>
          <Description>{DescriptionText}</Description>
        </TextWrapper>

        <ButtonWrapper isWidget={isWidget}>
          <ButtonNew onClick={handleOpenAccess}>{ButtonNewText}</ButtonNew>

          <If condition={isRenewal}>
            {/* @ts-expect-error */}
            <ButtonContinue disabled={isLoading} onClick={handleExtendAccess}>
              {ButtonContinueText}
            </ButtonContinue>
          </If>
        </ButtonWrapper>
      </PaywallWrapper>

      <OfferingSubscription
        defaultVisible={modalVisible}
        onVisibleChange={handleSetModal}
      />
    </>
  );
}

export default Paywall;
